// @ts-nocheck
// ═══ Visualizer — Cinematic Demo (Three Acts, 36 seconds) ═══
// General demo: showcases the visualizer tool itself, not tied to any artist.
//
// Act 1 — AWE (0-15s): heartbeat + sub-bass + particles + ambient presence.
// Act 2 — REVEAL (15-25s): canvas wakes, text choreography, beats bloom, ghost UI.
// Act 3 — INVITATION (25-36s): silence wall at 25.000s, Your Turn, UI emergence.
//
// Visual choreography is time-locked to the 36s grid. Audio feeds Act 2 reactivity
// but the demo renders without it (so it works before Timbre's track lands).

import {
  AbsoluteFill,
  Audio,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Easing,
} from 'remotion'
import { AudioReactivityProvider, useAudioReactivity } from './AudioReactivity'
import { BeatProvider, useBeatContext } from './BeatDetection'
import { AudioParticles } from './Particles'

// ═══ Constants (from spec v1) ═══
const DEMO_DURATION_S = 36
const FPS = 30
export const DEMO_DURATION_FRAMES = DEMO_DURATION_S * FPS // 1080

const ACT_1_END_S = 15
const ACT_2_END_S = 25
// const ACT_3_END_S = 36

const SILENCE_WALL_S = 25.0 // Act 2 → 3 hard transition
const WALKTHROUGH_HANDOFF_S = 33.0 // driver.js takes over

// Palette (purple/cyan/warm-white — tool identity, not artist-specific)
const COLOR_BG = '#05060A'
const COLOR_AMBER = '#856FC8'
const COLOR_WARM_WHITE = '#F0EAFF'
const COLOR_PINK_WASH = '#6FB8D6'

// ═══ Exported props ═══
export interface CinematicDemoProps {
  audioUrl?: string // Timbre's 36s track. Optional — demo renders without it.
  lyrics?: {
    title: string // Act 2 opener (default "14 Years")
    chorus: string // Act 2 mid (default "You and I are the same thing")
    refrain: string // Act 2 peak (default "We are the same thing")
  }
  prompt?: string // Act 3 (default "Your Turn.")
}

const DEFAULT_LYRICS = {
  title: 'Your Music',
  chorus: 'Brought to life',
  refrain: 'In motion',
}

// ═══ Composition entry ═══
export const CinematicDemo: React.FC<CinematicDemoProps> = ({
  audioUrl,
  lyrics = DEFAULT_LYRICS,
  prompt = 'Your Turn.',
}) => {
  return (
    <AudioReactivityProvider audioUrl={audioUrl}>
      <BeatProvider audioUrl={audioUrl} currentMs={0}>
        <DemoStage lyrics={lyrics} prompt={prompt} audioUrl={audioUrl} />
      </BeatProvider>
    </AudioReactivityProvider>
  )
}

// ═══ Stage ═══
// Particle field is mounted once at the stage level and interpolates count /
// opacity / audio-reactivity across all three acts. Per Timbre (v1.1 spec):
// Act 2 is *continuation* not restart — the field grows, it doesn't reset.
const DemoStage: React.FC<{
  lyrics: NonNullable<CinematicDemoProps['lyrics']>
  prompt: string
  audioUrl?: string
}> = ({ lyrics, prompt, audioUrl }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = frame / fps // seconds

  // Particle count — continuous across all acts (Timbre continuity rule).
  // Act 1: 0 → 80 (accumulation). Act 2: 80 → 160 (bloom at kick). Act 3: 160 → 80 → 60 (fade).
  const particleCount = Math.floor(
    interpolate(
      t,
      [0, 4.5, 6.0, 7.5, 9.0, 12.0, 15.0, 15.8, 23.0, 25.0, 31.5, 36.0],
      [0, 1, 8, 30, 60, 80, 80, 140, 160, 150, 90, 60],
      {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing: Easing.out(Easing.quad),
      }
    )
  )

  // Particle speed — slow in Act 1, bloom in Act 2, suspended in Act 3
  const particleSpeed = interpolate(
    t,
    [0, 15.0, 16.5, 23.0, 25.0, 25.3, 36.0],
    [0.08, 0.12, 0.35, 0.38, 0.38, 0.05, 0.04],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )

  // Particle opacity — master envelope
  const particleOpacity = interpolate(
    t,
    [0, 2.0, 12.0, 15.0, 23.0, 25.0, 25.3, 31.5, 36.0],
    [0, 0.5, 0.7, 0.75, 0.85, 0.8, 0.55, 0.25, 0.18],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )

  // Audio-reactivity flips on at Act 2 kick, off at silence wall
  const audioReactive = t >= 15.0 && t < 25.0

  return (
    <AbsoluteFill style={{ backgroundColor: COLOR_BG, overflow: 'hidden' }}>
      {audioUrl && <Audio src={audioUrl} />}

      {/* Unified particle field — one instance, continuous across all acts */}
      {particleCount > 0 && (
        <AudioParticles
          config={{
            count: particleCount,
            color: COLOR_AMBER,
            speed: particleSpeed,
            sizeRange: [1, 4],
            audioReactive,
          }}
          opacity={particleOpacity}
        />
      )}

      {/* Act 1 — AWE (0 → 15s) — heartbeat, vignette, "Listen." */}
      {t < ACT_1_END_S + 0.3 && <Act1Awe />}

      {/* Act 2 — REVEAL (15 → 25s) — color wash, beats, lyrics, ghost UI.
          Hold +0.2s past silence wall (200ms visual inertia matching Timbre's
          300ms silence window; refrain ghost fades by 25.2s so silence 25.0–25.3
          is unchallenged). */}
      {t >= ACT_1_END_S - 0.3 && t < ACT_2_END_S + 0.2 && (
        <Act2Reveal lyrics={lyrics} />
      )}

      {/* Act 3 — INVITATION (25 → 36s) — "Your Turn.", UI emergence hint */}
      {t >= SILENCE_WALL_S - 0.1 && <Act3Invitation prompt={prompt} />}
    </AbsoluteFill>
  )
}

// ═══ ACT 1 — AWE ═══
// Particles live at the stage level (continuity). This layer handles heartbeat
// radial glow, vignette, and the "Listen." text moment.
const Act1Awe: React.FC = () => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = frame / fps

  // Heartbeat pulses at spec times — embodied gestures, not a tempo grid.
  // 54 BPM informs envelope decay length only (per Timbre v1.1).
  // Pulses at 1.5 / 3.0 / 6.0 = thumps (short rise, match Timbre's audio thumps).
  // Pulses at 9.0 / 13.5 = drone swells (long rise, no thump in audio — just
  // the sub-bass drone breathing under the F# Aeolian wash).
  const heartbeatOpacity = heartbeatEnvelope(t, [
    { at: 1.5, strength: 0.25, decay: 1.4, rise: 0.2 },
    { at: 3.0, strength: 0.45, decay: 1.6, rise: 0.2 },
    { at: 6.0, strength: 0.55, decay: 1.5, rise: 0.2 },
    { at: 9.0, strength: 0.30, decay: 2.2, rise: 0.6 }, // swell
    { at: 13.5, strength: 0.38, decay: 2.6, rise: 0.6 }, // swell
  ])

  // "Listen." appears at 12.0s, holds, fades by 13.5s
  const listenOpacity = interpolate(
    t,
    [11.8, 12.6, 13.2, 13.6],
    [0, 0.4, 0.4, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )

  return (
    <AbsoluteFill>
      {/* Heartbeat radial glow */}
      <AbsoluteFill
        style={{
          background: `radial-gradient(circle at 50% 50%, ${COLOR_AMBER}${toHex(heartbeatOpacity * 0.55)} 0%, transparent 55%)`,
          pointerEvents: 'none',
        }}
      />

      {/* "Listen." — Playfair Display Italic, center */}
      <AbsoluteFill
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            fontFamily: 'Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: 72,
            fontWeight: 500,
            color: COLOR_WARM_WHITE,
            opacity: listenOpacity,
            letterSpacing: '0.05em',
            textShadow: `0 0 40px ${COLOR_AMBER}40`,
          }}
        >
          Watch.
        </div>
      </AbsoluteFill>

      {/* Vignette edge */}
      <AbsoluteFill
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)',
          pointerEvents: 'none',
        }}
      />
    </AbsoluteFill>
  )
}

// ═══ ACT 2 — REVEAL ═══
// Particle field continues from Act 1 (lives at stage level). This layer adds
// the kick bloom, color wash, beat reactivity, lyric choreography, ghost UI.
// Per Timbre v1.1: kick is a BLOOM of the same key, not a fresh song.
const Act2Reveal: React.FC<{
  lyrics: NonNullable<CinematicDemoProps['lyrics']>
}> = ({ lyrics }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = frame / fps

  // Kick bloom at 15.0s — softer than an explosion, continuity-preserving
  const kickBurst = interpolate(t, [14.9, 15.1, 15.9], [0, 0.7, 0.15], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  // Color wash intensifies 15 → 23s
  const washOpacity = interpolate(t, [15, 20, 23, 25], [0.15, 0.35, 0.55, 0.3], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  // Lyric block timing (seconds):
  //   "14 Years"            — 15.8 → 18.0 (types in 40ms stagger, holds, dissolves)
  //   "You and I..."        — 18.5 → 22.5
  //   "We are the same..."  — 23.0 → 25.0 (zero out by 25.2, not 25.5 — inertia tighten)
  const titleOpacity = interpolate(
    t,
    [15.7, 16.3, 17.7, 18.2],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )
  const chorusOpacity = interpolate(
    t,
    [18.4, 19.0, 22.2, 22.6],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )
  const refrainOpacity = interpolate(
    t,
    [22.8, 23.3, 24.6, 25.0, 25.2],
    [0, 1, 1, 0.15, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )

  // Refrain has a zoom-in animation (peak)
  const refrainScale = interpolate(t, [22.8, 24.5], [0.9, 1.12], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.cubic),
  })

  // Ghost timeline UI fades in at 21.5s (bottom edge, 12% opacity)
  const ghostTimelineOpacity = interpolate(t, [21.3, 22.5, 25.0], [0, 0.12, 0.12], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  return (
    <AbsoluteFill>
      {/* Kick burst flash */}
      <AbsoluteFill
        style={{
          backgroundColor: COLOR_WARM_WHITE,
          opacity: kickBurst * 0.08,
          pointerEvents: 'none',
        }}
      />

      {/* Color wash — pink over amber base */}
      <AbsoluteFill
        style={{
          backgroundColor: COLOR_PINK_WASH,
          opacity: washOpacity,
          mixBlendMode: 'soft-light',
          pointerEvents: 'none',
        }}
      />

      {/* Particles continue from Act 1 — field lives at stage level.
          Beat-reactive flash layer renders here, over the wash. */}
      <BeatFlash />

      {/* Lyric: "14 Years" */}
      <LyricCard
        text={lyrics.title}
        opacity={titleOpacity}
        fontSize={140}
        fontWeight={600}
        color={COLOR_WARM_WHITE}
      />

      {/* Lyric: chorus */}
      <LyricCard
        text={lyrics.chorus}
        opacity={chorusOpacity}
        fontSize={84}
        fontWeight={500}
        color={COLOR_WARM_WHITE}
      />

      {/* Lyric: refrain (zoomed) */}
      <LyricCard
        text={lyrics.refrain}
        opacity={refrainOpacity}
        fontSize={96}
        fontWeight={600}
        color={COLOR_AMBER}
        scale={refrainScale}
      />

      {/* Ghost timeline (spec 21.5s) — bottom edge */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 48,
          opacity: ghostTimelineOpacity,
          pointerEvents: 'none',
        }}
      >
        <GhostTimeline t={t} />
      </div>
    </AbsoluteFill>
  )
}

// ═══ ACT 3 — INVITATION ═══
// Particle field at stage level handles the Act 3 fade. This layer handles the
// "Your Turn." text, caret, and UI emergence hint.
const Act3Invitation: React.FC<{ prompt: string }> = ({ prompt }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = frame / fps

  // "Your Turn." fades in at 26.0s (1.8s fade)
  const promptOpacity = interpolate(
    t,
    [26.0, 27.8, 32.5, 33.5],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )

  // Caret blinks below prompt from 27.8s onward
  const caretVisible = t >= 27.8 && t < 33.0 && Math.floor((t - 27.8) * 2) % 2 === 0

  // UI emergence layer placeholder (real UI materializes in Player wrapper, not here)
  // This layer renders a faint rectangle hint where the Studio UI will appear.
  const uiHintOpacity = interpolate(t, [30.0, 31.5], [0, 0.25], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  return (
    <AbsoluteFill>
      {/* Particles fade out via stage-level opacity interpolation */}

      {/* "Your Turn." */}
      <AbsoluteFill
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            fontFamily: 'Playfair Display, serif',
            fontStyle: 'italic',
            fontSize: 88,
            fontWeight: 500,
            color: COLOR_WARM_WHITE,
            opacity: promptOpacity,
            letterSpacing: '0.03em',
            textShadow: `0 0 60px ${COLOR_AMBER}30`,
          }}
        >
          {prompt}
        </div>

        {/* Caret */}
        <div
          style={{
            width: 2,
            height: 36,
            backgroundColor: COLOR_WARM_WHITE,
            opacity: caretVisible ? 0.7 : 0,
            marginTop: 24,
            transition: 'opacity 0.1s',
          }}
        />
      </AbsoluteFill>

      {/* UI emergence hint (real mount happens in CinematicDemoPlayer) */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 120,
          background: `linear-gradient(to top, ${COLOR_BG}CC, transparent)`,
          opacity: uiHintOpacity,
          pointerEvents: 'none',
        }}
      />
    </AbsoluteFill>
  )
}

// ═══ Helpers ═══

// Heartbeat envelope: sum of rise-and-fall bumps at specified beats.
// Per-pulse `rise` parameter — short rise (150–200 ms) reads as a thump;
// long rise (400–600 ms) reads as a drone swell, not a heartbeat.
// Thumps land at 1.5 / 3.0 / 6.0 s (match Timbre's sub-bass thumps);
// swells land at 9.0 / 13.5 s (match drone bed breathing under the overtones).
function heartbeatEnvelope(
  t: number,
  beats: { at: number; strength: number; decay: number; rise?: number }[]
): number {
  let sum = 0
  for (const b of beats) {
    const rise = b.rise ?? 0.2 // default 200 ms rise (thump)
    const halfRise = rise / 2
    const dt = t - b.at
    if (dt < -halfRise) continue
    if (dt < halfRise) {
      sum += b.strength * Math.max(0, (dt + halfRise) / rise) // rise phase
    } else {
      sum += b.strength * Math.exp(-(dt - halfRise) / b.decay) // fall phase
    }
  }
  return Math.min(1, sum)
}

// Opacity → hex alpha
function toHex(n: number): string {
  const clamped = Math.max(0, Math.min(1, n))
  return Math.round(clamped * 255)
    .toString(16)
    .padStart(2, '0')
}

// ═══ Sub-components ═══

const LyricCard: React.FC<{
  text: string
  opacity: number
  fontSize: number
  fontWeight: number
  color: string
  scale?: number
}> = ({ text, opacity, fontSize, fontWeight, color, scale = 1 }) => {
  if (opacity <= 0.01) return null
  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          fontFamily: 'Playfair Display, Georgia, serif',
          fontSize,
          fontWeight,
          color,
          opacity,
          letterSpacing: '0.01em',
          textAlign: 'center',
          maxWidth: '80%',
          lineHeight: 1.15,
          textShadow: `0 4px 24px rgba(0,0,0,0.6)`,
          transform: `scale(${scale})`,
          transformOrigin: 'center',
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  )
}

const BeatFlash: React.FC = () => {
  const { beatIntensity } = useBeatContext()
  if (beatIntensity < 0.15) return null
  return (
    <AbsoluteFill
      style={{
        backgroundColor: COLOR_WARM_WHITE,
        opacity: beatIntensity * 0.05,
        pointerEvents: 'none',
        mixBlendMode: 'screen',
      }}
    />
  )
}

const GhostTimeline: React.FC<{ t: number }> = ({ t }) => {
  // Fake timeline — rule, playhead marker, section color bars
  // Demonstrates "you're watching something that was directed"
  const playheadX = interpolate(t, [21.5, 25.0], [0.35, 0.68], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Section bars */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: '10%',
          right: '10%',
          height: 4,
          display: 'flex',
          gap: 4,
        }}
      >
        <div style={{ flex: 2, backgroundColor: '#4A5B8C', opacity: 0.6 }} />
        <div style={{ flex: 3, backgroundColor: COLOR_AMBER, opacity: 0.7 }} />
        <div style={{ flex: 4, backgroundColor: COLOR_PINK_WASH, opacity: 0.6 }} />
        <div style={{ flex: 2, backgroundColor: '#2A3550', opacity: 0.5 }} />
      </div>
      {/* Playhead */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: `${10 + playheadX * 80}%`,
          width: 1,
          height: 36,
          backgroundColor: COLOR_WARM_WHITE,
          opacity: 0.8,
        }}
      />
    </div>
  )
}

export default CinematicDemo
